import { MoveMoneyTypes } from './consts';
import { FEATURE_FLAG } from '../../constants/general';
import { IMoveMoneyLimits } from './api/getWireLimits';
import { ISupplierModel } from '../Supplier/types';
import { IMoveMoneyData } from '../../store/createMoneyMoveSlice';
import { featureAccess, LimitedFeatureType } from '../../services/appAccess.service';
import { FeatureAccess } from '../AccountSettings/components/rolesAndPermissions/PermissionsList';

interface IInternationalData {
  swiftCode: string;
  iban: string;
  internationalPhoneNumber: string;
  internationalSupplierAddressData: {
    city: string;
    state: string | null;
    line1: string;
    line2?: string | null;
    postalCode: string;
    country: string;
  };
}

export const validateFeature = (moveType: MoveMoneyTypes) => {
  switch (moveType) {
    case MoveMoneyTypes.WIRE_OUT:
      return (
        featureAccess(FEATURE_FLAG.DOMESTIC_WIRE_OUT, LimitedFeatureType.SendMoneyWireOut) === FeatureAccess.READ_WRITE
      );
    case MoveMoneyTypes.MAIL_CHECK:
      return (
        featureAccess(FEATURE_FLAG.MAIL_CHECK, LimitedFeatureType.SendMoneyMailACheck) === FeatureAccess.READ_WRITE
      );

    case MoveMoneyTypes.INTERNATIONAL_WIRE_OUT:
      return (
        featureAccess(FEATURE_FLAG.INTERNATIONAL_WIRE_OUT, LimitedFeatureType.SendMoneyWireOut) ===
        FeatureAccess.READ_WRITE
      );

    case MoveMoneyTypes.ACH_OUT_TRANSFER:
      return featureAccess(FEATURE_FLAG.NEW_ACH_OUT, LimitedFeatureType.SendMoneyACH) === FeatureAccess.READ_WRITE;

    default:
      return false;
  }
};

export const getVendorMethod = (moveType: MoveMoneyTypes) => {
  switch (moveType) {
    case MoveMoneyTypes.WIRE_OUT:
      return 'WIRE';

    case MoveMoneyTypes.MAIL_CHECK:
      return 'CHECK';

    default:
      return '';
  }
};

export const isBusinessOrRecipientNameValid = (name: string) => {
  const words = name.trim().split(/\s+/);
  let isAnyWordInvalid = false;

  if (words.length < 2) {
    return false;
  }

  words.forEach((word) => {
    /** In case will need at least 1 letter */
    // !/^[a-zA-Z0-9 ]+$/.test(word) || !/[a-zA-Z]/.test(word)
    if (!/^[a-zA-Z0-9 ]+$/.test(word)) {
      isAnyWordInvalid = true;
    }
  });

  return isAnyWordInvalid ? false : words[0].length >= 2 && words[1].length >= 2;
};

export const isVendorWireDataValid = (vendor: ISupplierModel) =>
  !!(vendor?.wireAccountData && vendor?.wireAccountData?.accountNumber && vendor?.wireAccountData?.routingNumber);

export const isVendorAddressValid = (vendor: ISupplierModel) => {
  if (!vendor?.supplierAddressData) return false;
  const { line1, state, city, postalCode } = vendor.supplierAddressData;
  return !!vendor?.supplierAddressData && !!line1 && !!state && !!city && !!postalCode;
};

export const isInternationalDataValid = (intlData: IInternationalData | null | undefined) => {
  if (!intlData) return false;
  const {
    iban,
    swiftCode,
    internationalSupplierAddressData: { line1, city, country, postalCode },
  } = intlData;
  const mandatoryIntlData = [iban, swiftCode, line1, city, country, postalCode];

  return mandatoryIntlData.every((value) => value);
};

export const isVendorAchDataValid = (vendor: ISupplierModel) => {
  if (!vendor?.achAccountData) return false;
  const { accountNumber, routingNumber } = vendor.achAccountData;
  return !!accountNumber && !!routingNumber;
};

export const findLowestLimit = (obj: IMoveMoneyLimits | undefined) => {
  let lowestKey = '';
  let lowestValue = Infinity;

  if (!obj) return { key: lowestKey, value: lowestValue };

  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(obj)) {
    if (value && (value < lowestValue || (value === lowestValue && key < lowestKey))) {
      lowestKey = key;
      lowestValue = value;
    }
  }

  return { key: lowestKey, value: lowestValue };
};

export const getSelectedAchFee = (moveMoneyData: IMoveMoneyData) => {
  const { achOutFeeData } = moveMoneyData;
  if (!achOutFeeData) return undefined;
  return achOutFeeData?.fees.find((feeItem) => feeItem.achTransferType === moveMoneyData.achOutTransferType);
};
